import { useEffect, useState, useCallback } from "react";
import { Trash2, Plus, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { adminApi } from "../../lib/api";
import { inputCls, labelCls } from "./shared";

export function UsersTab() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "editor", password: "" });

  const load = useCallback(() => {
    adminApi().get("/admin/users").then((r) => setItems(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      await adminApi().post("/admin/users", form);
      toast.success("User created"); setCreating(false);
      setForm({ email: "", name: "", role: "editor", password: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
  };

  const setRole = async (id, role) => {
    try { await adminApi().put(`/admin/users/${id}`, { role }); toast.success(`Role → ${role}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const setStatus = async (id, status) => {
    try { await adminApi().put(`/admin/users/${id}`, { status }); toast.success(`${status}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const resetPw = async (id, email) => {
    if (!window.confirm(`Reset password for ${email}? This will invalidate their current password.`)) return;
    try {
      const { data } = await adminApi().post(`/admin/users/${id}/reset-password`);
      // Show in a persistent toast + copy
      window.prompt("New temporary password (copy now, will not be shown again):", data.temp_password);
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
  };

  const del = async (id, email) => {
    if (!window.confirm(`Delete user ${email}?`)) return;
    try { await adminApi().delete(`/admin/users/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button data-testid="admin-add-user-btn" onClick={() => setCreating(true)} className="flex items-center gap-2 bg-[#970C10] hover:bg-[#7A0A0D] text-white text-sm font-bold px-4 py-2 rounded-lg">
          <Plus className="w-4 h-4" /> New user
        </button>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr><th className="px-4 py-3">Email / Name</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Last login</th><th className="px-4 py-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50/70 transition-colors duration-150" data-testid={`admin-user-row-${u.email}`}>
                <td className="px-4 py-3">
                  <p className="font-semibold text-slate-900">{u.name || "—"}</p>
                  <p className="text-xs text-slate-500 font-mono">{u.email}</p>
                </td>
                <td className="px-4 py-3">
                  <select value={u.role} onChange={(e) => setRole(u.id, e.target.value)} className="text-xs border border-slate-200 rounded px-2 py-1" data-testid={`user-role-${u.email}`}>
                    <option value="owner">owner</option><option value="editor">editor</option><option value="viewer">viewer</option>
                  </select>
                </td>
                <td className="px-4 py-3">
                  <select value={u.status} onChange={(e) => setStatus(u.id, e.target.value)} className="text-xs border border-slate-200 rounded px-2 py-1" data-testid={`user-status-${u.email}`}>
                    <option value="active">active</option><option value="paused">paused</option>
                  </select>
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "never"}</td>
                <td className="px-4 py-3 text-right space-x-2">
                  <button data-testid={`user-reset-${u.email}`} onClick={() => resetPw(u.id, u.email)} title="Reset password" className="text-slate-500 hover:text-amber-600"><KeyRound className="w-4 h-4 inline" /></button>
                  <button data-testid={`user-delete-${u.email}`} onClick={() => del(u.id, u.email)} className="text-slate-500 hover:text-red-600"><Trash2 className="w-4 h-4 inline" /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No users yet. Create the first team member.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={creating} onOpenChange={(v) => !v && setCreating(false)}>
        <DialogContent className="sm:max-w-md" data-testid="admin-user-dialog">
          <DialogHeader><DialogTitle className="font-heading">New user</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className={labelCls}>Email</label><input className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" /></div>
            <div><label className={labelCls}>Name</label><input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" /></div>
            <div><label className={labelCls}>Role</label>
              <select className={inputCls} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role-select">
                <option value="owner">owner — full access + user mgmt</option>
                <option value="editor">editor — all CRUD except users/settings</option>
                <option value="viewer">viewer — read-only</option>
              </select>
            </div>
            <div><label className={labelCls}>Temporary password (min 8 chars)</label><input type="password" className={inputCls} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" /></div>
          </div>
          <button onClick={create} data-testid="user-create-btn" className="w-full bg-[#970C10] hover:bg-[#7A0A0D] text-white font-bold py-2.5 rounded-lg">Create user</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
